"use client"

import { useState } from "react"
import { Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  ArrowRight,
  Sparkles,
  Zap,
  Calendar,
  Users,
  Star,
  Shield,
  CheckCircle,
  TrendingUp,
  Rocket,
  Heart
} from "lucide-react"

interface Stat {
  number: string
  label: string
  icon: React.ComponentType<{ className?: string }>
}

const stats: Stat[] = [
  { number: "2.5M+", label: "Events Created", icon: Calendar },
  { number: "50K+", label: "Active Organizers", icon: Users },
  { number: "20s", label: "Avg. Generation", icon: Zap },
  { number: "99.9%", label: "Uptime", icon: Shield }
]

const highlights = [
  { icon: Zap, label: "20 Second Generation", desc: "AI-powered creation" },
  { icon: Star, label: "50+ Templates", desc: "Industry-specific" },
  { icon: Users, label: "Team Management", desc: "Analytics included" }
]

export function CTASection() {
  const [hoveredStat, setHoveredStat] = useState<number | null>(null)

  return (
    <section className="relative py-24 overflow-hidden bg-background">
      {/* Animated Background */}
      <div className="absolute inset-0 opacity-30 dark:opacity-20">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(90deg, rgba(var(--theme-primary), 0.02) 1px, transparent 1px),
              linear-gradient(rgba(var(--theme-primary), 0.02) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px'
          }}
        />
      </div>

      {/* Floating Gradient Orbs */}
      <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full opacity-10 blur-3xl animate-pulse"
        style={{
          background: `radial-gradient(circle, rgb(var(--theme-primary)) 0%, transparent 70%)`,
          animationDuration: '8s'
        }}
      />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full opacity-10 blur-3xl animate-pulse"
        style={{
          background: `radial-gradient(circle, rgb(var(--theme-secondary)) 0%, transparent 70%)`,
          animationDuration: '6s',
          animationDelay: '2s'
        }}
      />
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full opacity-5 blur-3xl"
        style={{
          background: `radial-gradient(circle, rgb(var(--theme-accent)) 0%, transparent 70%)`
        }}
      />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Main CTA Container */}
        <div className="relative animate-in fade-in slide-in-from-bottom duration-700">
          {/* Glowing border effect */}
          <div
            className="absolute inset-0 rounded-3xl opacity-20 blur-xl"
            style={{
              background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`
            }}
          />

          <div className="relative rounded-3xl glass-card border backdrop-blur-xl p-12 text-center shadow-2xl"
            style={{ borderColor: `rgba(var(--theme-primary), 0.3)` }}
          >
            {/* Top badge */}
            <Badge
              className="mb-6 text-white border-0 shadow-lg px-4 py-2"
              style={{
                background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`
              }}
            >
              <Star className="mr-2 h-4 w-4 fill-white" />
              Join 50,000+ Event Professionals
            </Badge>

            {/* Main heading */}
            <h2 className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl mb-6 animate-in fade-in slide-in-from-bottom duration-700 delay-100">
              Ready to Build{" "}
              <span
                className="relative inline-block"
                style={{
                  background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}
              >
                Amazing Events
                <svg
                  className="absolute -bottom-2 left-0 w-full"
                  height="8"
                  viewBox="0 0 200 8"
                  fill="none"
                >
                  <path
                    d="M1 5.5C50 2.5 150 2.5 199 5.5"
                    stroke={`rgb(var(--theme-accent))`}
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                </svg>
              </span>{" "}
              in Seconds?
            </h2>

            {/* Description */}
            <p className="mx-auto max-w-2xl text-xl text-muted-foreground mb-12 leading-relaxed animate-in fade-in slide-in-from-bottom duration-700 delay-200">
              Join thousands of event organizers who are already creating beautiful, professional event platforms
              with AI. From small gatherings to large conferences, transform your ideas into reality instantly.
            </p>

            {/* Feature highlights */}
            <div className="flex flex-wrap justify-center gap-8 mb-12 animate-in fade-in slide-in-from-bottom duration-700 delay-300">
              {highlights.map((highlight, index) => {
                const IconComponent = highlight.icon
                return (
                  <div key={index} className="group flex items-center space-x-3 cursor-pointer">
                    <div
                      className="flex h-12 w-12 items-center justify-center rounded-full shadow-lg transition-all duration-500 group-hover:scale-110 group-hover:rotate-12"
                      style={{
                        background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`
                      }}
                    >
                      <IconComponent className="h-6 w-6 text-white" />
                    </div>
                    <div className="text-left">
                      <div className="font-semibold text-foreground group-hover:text-foreground transition-colors">
                        {highlight.label}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {highlight.desc}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* CTA buttons */}
            <div className="flex flex-col items-center justify-center gap-4 sm:flex-row sm:gap-6 mb-8 animate-in fade-in slide-in-from-bottom duration-700 delay-400">
              <Link to="/signup">
                <Button
                  size="lg"
                  className="group text-white shadow-2xl hover:shadow-3xl transition-all duration-300 text-lg px-12 py-7 rounded-xl hover:scale-105"
                  style={{
                    background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`
                  }}
                >
                  <Sparkles className="mr-2 h-5 w-5 group-hover:rotate-12 transition-transform" />
                  Start Building Free
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>

              <Link to="/templates">
                <Button
                  variant="outline"
                  size="lg"
                  className="text-lg px-12 py-7 rounded-xl hover:shadow-lg transition-all duration-300 hover:scale-105 border-2"
                  style={{
                    borderColor: `rgba(var(--theme-primary), 0.3)`
                  }}
                >
                  Browse Templates
                </Button>
              </Link>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap justify-center gap-6 text-sm animate-in fade-in slide-in-from-bottom duration-700 delay-500">
              {[
                { icon: CheckCircle, text: "10 free AI generations", color: "text-green-500" },
                { icon: CheckCircle, text: "No credit card required", color: "text-green-500" },
                { icon: Star, text: "4.9/5 user rating", color: "text-yellow-500 fill-yellow-500" }
              ].map((indicator, index) => {
                const IconComponent = indicator.icon
                return (
                  <div key={index} className="flex items-center space-x-2 text-muted-foreground group cursor-pointer">
                    <IconComponent className={`h-4 w-4 ${indicator.color} group-hover:scale-125 transition-transform`} />
                    <span className="group-hover:text-foreground transition-colors">{indicator.text}</span>
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        {/* Social proof */}
        <div className="mt-12 text-center animate-in fade-in slide-in-from-bottom duration-700 delay-600">
          <div className="inline-flex items-center gap-2 glass-card rounded-full px-6 py-3 border"
            style={{
              borderColor: `rgba(var(--theme-primary), 0.3)`,
              background: `rgba(var(--theme-primary), 0.05)`
            }}
          >
            <Heart className="h-4 w-4" style={{ color: `rgb(var(--theme-primary))` }} />
            <p className="text-muted-foreground text-lg">
              <span className="font-semibold text-foreground">2.5M+ events</span> created • Trusted by organizers worldwide
            </p>
          </div>
        </div>

        {/* Bottom stats section */}
        <div className="mt-20 grid grid-cols-2 gap-8 sm:grid-cols-4 animate-in fade-in slide-in-from-bottom duration-700 delay-700">
          {stats.map((stat, index) => {
            const IconComponent = stat.icon
            const isHovered = hoveredStat === index

            return (
              <div
                key={index}
                className="group text-center cursor-pointer"
                onMouseEnter={() => setHoveredStat(index)}
                onMouseLeave={() => setHoveredStat(null)}
              >
                <div className="mb-4 flex justify-center">
                  <div
                    className="flex h-14 w-14 items-center justify-center rounded-xl shadow-lg transition-all duration-500 group-hover:scale-110 group-hover:rotate-6"
                    style={{
                      background: isHovered
                        ? `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`
                        : `rgba(var(--theme-primary), 0.1)`
                    }}
                  >
                    <IconComponent
                      className="h-7 w-7 transition-colors duration-500"
                    />
                  </div>
                </div>
                <div
                  className="text-3xl font-bold mb-2 sm:text-4xl transition-all duration-500 group-hover:scale-110"
                  style={{
                    background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}
                >
                  {stat.number}
                </div>
                <div className="text-sm text-muted-foreground font-medium uppercase tracking-wide group-hover:text-foreground transition-colors">
                  {stat.label}
                </div>
              </div>
            )
          })}
        </div>

        {/* Final Trust Badge */}
        <div className="mt-16 text-center animate-in fade-in slide-in-from-bottom duration-700 delay-800">
          <div className="inline-flex items-center gap-3 glass-card rounded-full px-6 py-3 border"
            style={{
              borderColor: `rgba(var(--theme-primary), 0.3)`,
              background: `rgba(var(--theme-primary), 0.05)`
            }}
          >
            <Rocket className="h-5 w-5" style={{ color: `rgb(var(--theme-primary))` }} />
            <span className="text-sm font-semibold text-foreground">
              Launch your event platform in minutes, not months
            </span>
          </div>
        </div>
      </div>
    </section>
  )
}
